import bindAllMethods from '../../../utils/bindAllMethods';
import type IExchangeTokenService from './IExchangeTokenService';
import type IAuthTokenService from '../../authTokenService/IAuthTokenService';
import { inject, singleton } from 'tsyringe';
import { AuthContextEnum, AuthTokenService } from '../../authTokenService';
import { internalLogger } from '../../../interface/v1/clientOsLogger';
import { TenantStrategyEnum } from '../../tenantHandler/strategy/TenantStrategyEnum';
import {
  ExchangeTokenDTOType,
  ExchangeV3Client,
  IExchangeClient
} from '../../../clients/shell/exchange';
import { JWebErrorHandler } from '../../JWeb';
import {
  AccessToken,
  AuthPluginError,
  TokenProviderOptions
} from '../../JWeb/types';
import type { ExchangeTokenInputType, LoginInputType } from '../types';
import {
  TokenLifetimeRequirementType,
  TokenType
} from '../../../services/JWeb/JWebEnums';
import AuthenticationProviderEnum from '../../../config/authenticationProviderEnum';
import { PlatformEnum } from '../../../infra/startup/types';
import type { AuthProvidersPropsType } from '../../../infra/startup/types';
import { AuthPluginFacade } from '../../JWeb/facades';
import InitializationTokenCache from '../../../services/JWeb/cache/InitializationTokenCache';

@singleton()
class ExchangeTokenServiceNative implements IExchangeTokenService {
  private _authTokenService: IAuthTokenService;
  private _exchangeClient: IExchangeClient;
  private _isSessionEnabled: boolean;
  private _platform: PlatformEnum;
  private _authProviderProps: AuthProvidersPropsType;

  constructor(
    @inject(AuthTokenService) authTokenService: IAuthTokenService,
    @inject(ExchangeV3Client) exchangeClient: IExchangeClient,
    @inject('LoginProps') loginProps: LoginInputType,
    @inject('Platform') platform: PlatformEnum,
    @inject('AuthProviderProps')
    authProviderProps: AuthProvidersPropsType
  ) {
    this._authTokenService = authTokenService;
    this._exchangeClient = exchangeClient;
    this._isSessionEnabled = loginProps.enabled;
    this._platform = platform;
    this._authProviderProps = authProviderProps;
    bindAllMethods(this);
  }

  public async exchangeJWeb(
    tenantId?: string,
    tokenOptions?: TokenProviderOptions
  ): Promise<AuthPluginError | AccessToken> {
    const jwebOptions = {
      tokenProviderOptions: {
        tokenType: TokenType.user,
        tokenLifetimeRequirements: [
          {
            type: TokenLifetimeRequirementType.preferredMinimumSecondsUntilExpiration,
            timeInterval: 60 * 10
          }
        ],
        ...tokenOptions
      } as TokenProviderOptions
    };

    if (tenantId) {
      jwebOptions.tokenProviderOptions['tenantID'] = tenantId;
    }

    const exchangeTenantTokenResponse = JWebErrorHandler<AccessToken>(
      await AuthPluginFacade.getToken(jwebOptions)
    );
    /* istanbul ignore next */
    internalLogger?.debug?.d3({
      className: 'ExchangeTokenServiceNative',
      methodName: 'exchangeJWeb',
      message: 'JWEB - Exchanged token',
      returnValue: exchangeTenantTokenResponse
    });
    return exchangeTenantTokenResponse;
  }

  private async _exchangeShell(
    tenantStrategy: TenantStrategyEnum,
    accessToken: string
  ): Promise<string> {
    const exchangeDTO: ExchangeTokenDTOType = {
      accessToken,
      tenantStrategy
    };
    const exchangedToken = await this._exchangeClient.exchangeToken(
      exchangeDTO
    );
    /* istanbul ignore next */
    internalLogger?.debug?.d3({
      className: 'ExchangeTokenServiceNative',
      methodName: '_exchangeShell',
      message: 'JSHELL - Exchanged token',
      returnValue: exchangedToken.accessToken
    });
    return exchangedToken.accessToken;
  }

  private _isTenantToken(authContext: AuthContextEnum) {
    const { token: accessToken } = this._authTokenService.getToken(authContext);
    return !!this._authTokenService.getTenantIdFromGivenToken(accessToken);
  }

  public async exchange(
    exchangeTokenInput: ExchangeTokenInputType
  ): Promise<AuthPluginError | AccessToken> {
    const { tenantId, authContext, tenantStrategy, jwebTokenOptions } =
      exchangeTokenInput;

    // Uncomment line below when testing the production native app running portals on the lower stacks overriding commons.
    // if (tenantStrategy === TenantStrategyEnum.stratusNative) return;

    if (!this._isSessionEnabled) {
      /* istanbul ignore next */
      internalLogger?.debug?.d4({
        className: 'ExchangeTokenServiceNative',
        methodName: 'exchange',
        message: 'isSessionEnabled property disabled, skipping exchange',
        variables: {
          _isSessionEnabled: this._isSessionEnabled,
          exchangeTokenInput
        }
      });
      return;
    }

    const isAppAuthProviderAuthz =
      this._authProviderProps.authenticationProvider ===
      AuthenticationProviderEnum.authz;

    const isPortalAuthProviderAuthz =
      this._authProviderProps.initialAuthenticationProvider ===
      AuthenticationProviderEnum.authz;

    // TODO: Today even that the token is orgaware, in native we save in tokenRepository as tenantless
    // TODO: in the furute we can get an orgless token from jweb, so this behavior will change to save the token in the right context in the tokenRepository
    const isTenantToken = this._isTenantToken(AuthContextEnum.tenantless);

    const isAppCoptorAndUserAlreadyHasUsingTenantId =
      this._authProviderProps.authenticationProvider ===
        AuthenticationProviderEnum.coptor &&
      !!isTenantToken &&
      this._platform !== PlatformEnum.nativeOnly;

    if (isAppCoptorAndUserAlreadyHasUsingTenantId) {
      internalLogger.log(
        'Coptor exchange - User already has a token with tenant, skipping exchange.',
        exchangeTokenInput
      );
      return;
    }

    internalLogger.log('Exchange tenant token', exchangeTokenInput);

    let token: string;
    let exchangeResult;
    try {
      const initializationToken =
        (await InitializationTokenCache.getToken()) as AccessToken;
      // This OR is a condition to fix a problem in the AA
      const initializationTokenTenantId =
        initializationToken?.tenantId || (initializationToken as any)?.tenantID;

      if (initializationTokenTenantId !== tenantId)
        exchangeResult = await this.exchangeJWeb(tenantId, jwebTokenOptions);

      if (exchangeResult?.error) {
        throw new Error(
          `Unable to exchange token using JWeb. Code: ${exchangeResult?.error?.code}, Reason: ${exchangeResult?.error?.reason}`
        );
      }
      token = exchangeResult?.tokenValue || initializationToken?.tokenValue;

      if (
        this._platform !== PlatformEnum.nativeOnly &&
        isAppAuthProviderAuthz &&
        isPortalAuthProviderAuthz
      ) {
        internalLogger.log(
          'Exchanging tenant token for JSHELL in Env and Portal Authz.'
        );

        token = await this._exchangeShell(tenantStrategy, token);
      }

      /* istanbul ignore next */
      internalLogger?.debug?.d1({
        className: 'ExchangeTokenServiceNative',
        methodName: 'exchange',
        variableName: 'token',
        message: 'Token set in exchange',
        currentValue: token
      });
      this._authTokenService.setToken(token, authContext);
    } catch (error) {
      internalLogger.error(error);
      throw new Error(error);
    }
    return exchangeResult;
  }
}
export default ExchangeTokenServiceNative;
